// The seam between the admin UI and its backend.
//
// `SupabaseDataSource` is the real one; `MockDataSource` keeps the whole app runnable
// without Docker or a Supabase stack. Same idea as `CatalogProviding` on the iOS side —
// no screen knows which it is talking to.

import type {
    CurrentUser, Exercise, MediaAsset, Module, ModuleVersion, Program, ProgramNode,
    Session, Skill, SkillStudy, Uuid,
} from './types';

export interface DataSource {
    // Auth
    signIn(email: string, password: string): Promise<CurrentUser>;
    signOut(): Promise<void>;
    currentUser(): Promise<CurrentUser | null>;

    // Modules
    listModules(): Promise<Module[]>;
    getModule(id: Uuid): Promise<Module | null>;
    createModule(input: Pick<Module, 'slug' | 'name' | 'studio_name' | 'category' | 'difficulty'>): Promise<Module>;
    updateModule(id: Uuid, patch: Partial<Module>): Promise<void>;

    // Exercises
    listExercises(moduleId: Uuid): Promise<Exercise[]>;
    upsertExercise(exercise: Partial<Exercise> & { module_id: Uuid }): Promise<Exercise>;
    deleteExercise(id: Uuid): Promise<void>;

    // Sessions
    listSessions(moduleId: Uuid): Promise<Session[]>;
    upsertSession(session: Partial<Session> & { module_id: Uuid }): Promise<Session>;
    deleteSession(id: Uuid): Promise<void>;

    // Programme
    getProgram(moduleId: Uuid): Promise<Program | null>;
    upsertProgram(program: Program): Promise<void>;
    listProgramNodes(moduleId: Uuid): Promise<ProgramNode[]>;
    upsertProgramNode(node: Partial<ProgramNode> & { module_id: Uuid }): Promise<ProgramNode>;
    deleteProgramNode(id: Uuid): Promise<void>;
    reorderProgramNodes(rows: { id: Uuid; sort_order: number }[]): Promise<void>;

    // Skills
    listSkills(moduleId: Uuid): Promise<Skill[]>;
    upsertSkill(skill: Partial<Skill> & { module_id: Uuid }): Promise<Skill>;
    deleteSkill(id: Uuid): Promise<void>;

    // Skill studies
    listSkillStudies(moduleId: Uuid): Promise<SkillStudy[]>;
    upsertSkillStudy(study: Partial<SkillStudy> & { module_id: Uuid }): Promise<SkillStudy>;
    deleteSkillStudy(id: Uuid): Promise<void>;

    // Media
    listMedia(moduleId: Uuid): Promise<MediaAsset[]>;
    getMedia(id: Uuid): Promise<MediaAsset | null>;
    /** Ask Mux for a direct-upload URL; the row starts in `uploading`. */
    createVideoUpload(moduleId: Uuid, title: string): Promise<{ mediaId: Uuid; uploadUrl: string }>;
    /** Register a video that already exists in Mux — a pasted playback id, ready at once. */
    registerMuxPlayback(moduleId: Uuid, playbackId: string, title: string): Promise<MediaAsset>;
    /** Push an image to Storage and register it, ready at once. */
    uploadImage(moduleId: Uuid, file: File): Promise<MediaAsset>;
    /** Public URL for a Storage-backed image, for previews. */
    imageUrl(storagePath: string): string;

    // Publishing
    validateModule(moduleId: Uuid): Promise<string[]>;
    publishModule(moduleId: Uuid, notes: string): Promise<number>;
    listVersions(moduleId: Uuid): Promise<ModuleVersion[]>;
    rollbackModule(moduleId: Uuid, version: number): Promise<void>;

    /** Where the data is coming from — surfaced in the UI so it is never ambiguous. */
    readonly label: string;
}

export class DataSourceError extends Error {
    constructor(message: string, readonly cause?: unknown) {
        super(message);
        this.name = 'DataSourceError';
    }
}
